// src/theme.js
// SOURCE UNIQUE DE VÉRITÉ pour le thème (clair/sombre).
// Toute l'app passe par ici : classes CSS, stockage, événements.
//
// - Classe `.dark`      -> Tailwind + composants récents
// - Classe `.dark-mode` -> CSS legacy (_variables.css et pages historiques)
// - Classe `.light`     -> compat composants qui la lisent
// Les deux conventions de classe sont toujours synchronisées ici pour éviter
// que la moitié de l'app reste en clair pendant que l'autre passe en sombre.

const CANON_KEY = 'fm:theme'; // clé canonique ('dark' | 'light')
// Clés historiques encore lues/écrites par d'anciens composants (compat).
const MODE_KEYS = [CANON_KEY, 'theme', 'fm_theme', 'fullmetrix:theme', 'fullmargin:theme', 'color-theme'];
const BOOL_KEYS = ['darkMode', 'isDarkMode', 'fm_dark_mode', 'fullmetrix:darkMode'];
// Événements écoutés par Sidebar / Header / ChatPage.
const THEME_EVENTS = ['fm:theme-change', 'fm:set-theme'];

export function normalizeTheme(value) {
  if (value == null) return null;
  if (typeof value === 'boolean') return value ? 'dark' : 'light';
  if (typeof value === 'object') {
    return normalizeTheme(value.mode ?? value.theme ?? value.value ?? value.dark);
  }
  const raw = String(value).trim().toLowerCase();
  if (['dark', 'sombre', 'night', '1', 'true', 'yes', 'on'].includes(raw)) return 'dark';
  if (['light', 'clair', 'day', '0', 'false', 'no', 'off'].includes(raw)) return 'light';
  return null;
}

export function getInitialTheme() {
  try {
    for (const key of MODE_KEYS) {
      const mode = normalizeTheme(localStorage.getItem(key));
      if (mode) return mode;
    }
    for (const key of BOOL_KEYS) {
      const mode = normalizeTheme(localStorage.getItem(key));
      if (mode) return mode;
    }
  } catch {}
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function writeAllKeys(mode) {
  const boolValue = String(mode === 'dark');
  try {
    MODE_KEYS.forEach((key) => localStorage.setItem(key, mode));
    BOOL_KEYS.forEach((key) => localStorage.setItem(key, boolValue));
  } catch {}
}

function emitThemeEvents(mode) {
  const detail = { mode, theme: mode, value: mode, dark: mode === 'dark', isDark: mode === 'dark' };
  THEME_EVENTS.forEach((name) => {
    // Objet complet pour les composants récents.
    window.dispatchEvent(new CustomEvent(name, { detail }));
    // String simple pour les anciens composants qui attendent "dark"/"light".
    window.dispatchEvent(new CustomEvent(name, { detail: mode }));
  });
}

export function applyTheme(theme, { persist = true, emit = true } = {}) {
  const mode = normalizeTheme(theme) || 'light';
  const isDark = mode === 'dark';
  const html = document.documentElement;
  const body = document.body;

  [html, body].filter(Boolean).forEach((node) => {
    node.classList.toggle('dark', isDark); // Tailwind + CSS récent
    node.classList.toggle('dark-mode', isDark); // CSS legacy (_variables.css)
    node.classList.toggle('light', !isDark);
    node.dataset.theme = mode;
    node.style.colorScheme = mode;
  });
  if (body) body.style.backgroundColor = isDark ? '#050816' : '';

  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) themeColor.setAttribute('content', isDark ? '#0b0d15' : '#ffffff');

  if (persist) writeAllKeys(mode);
  if (emit) emitThemeEvents(mode);
  return mode;
}

export function toggleTheme() {
  const next = document.documentElement.classList.contains('dark') ? 'light' : 'dark';
  applyTheme(next);
  return next;
}

// Bascule le thème avec une animation "vague" (révélation circulaire) qui part
// du point cliqué. Utilise la View Transitions API si dispo, sinon bascule simple.
export function toggleThemeWave(originX, originY) {
  const next = document.documentElement.classList.contains('dark') ? 'light' : 'dark';
  const doApply = () => applyTheme(next);

  const supported = typeof document !== 'undefined' && typeof document.startViewTransition === 'function';
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!supported || reduce || typeof originX !== 'number' || typeof originY !== 'number') {
    doApply();
    return next;
  }

  const root = document.documentElement;
  const maxR = Math.hypot(
    Math.max(originX, window.innerWidth - originX),
    Math.max(originY, window.innerHeight - originY)
  );
  root.style.setProperty('--fm-wave-x', `${originX}px`);
  root.style.setProperty('--fm-wave-y', `${originY}px`);
  root.style.setProperty('--fm-wave-r', `${maxR}px`);
  root.classList.add('fm-theme-wave');

  const vt = document.startViewTransition(doApply);
  vt.finished.finally(() => root.classList.remove('fm-theme-wave'));
  return next;
}
